import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getProyectosApiBaseUrl } from "../../utils/proyectosApiBaseUrl";

const getToken = () => {
  const enc = localStorage.getItem("authToken");
  return enc ? atob(enc) : "";
};

type Pendiente = {
  proyecto_id: number;
  codigo: string;
  nombre: string;
  estado_global: string | null;
  municipio: string | null;
  disenadores_disponibles: Array<{ usuario: string; nombre: string }>;
  asignacion_actual: Record<string, any> | null;
};

type TrazabilidadItem = {
  proyecto_id: number;
  codigo: string;
  nombre: string;
  municipio: string | null;
  estado_global: string | null;
  disenador_asignacion: Record<string, any>;
  revision_updated_at: string | null;
};

function fmtFecha(iso: string | null | undefined): string {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("es-CO", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return String(iso);
  }
}

function labelEstadoAsignacion(da: Record<string, any>): string {
  const st = String(da?.estado || "").trim();
  if (st === "asignado") {
    const nom = da.disenador_nombre || da.disenador_usuario || "—";
    return da.auto_asignado ? `${nom} (automático)` : String(nom);
  }
  if (st === "pendiente") return "Pendiente";
  return st || "—";
}

function chipColorEstado(da: Record<string, any>): "success" | "warning" | "default" {
  const st = String(da?.estado || "").trim();
  if (st === "asignado") return "success";
  if (st === "pendiente") return "warning";
  return "default";
}

export default function AsignacionDisenadorPanel() {
  const navigate = useNavigate();
  const baseUrl = getProyectosApiBaseUrl();
  const token = React.useMemo(() => getToken(), []);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [ok, setOk] = React.useState<string | null>(null);
  const [items, setItems] = React.useState<Pendiente[]>([]);
  const [trazabilidad, setTrazabilidad] = React.useState<TrazabilidadItem[]>([]);
  const [seleccion, setSeleccion] = React.useState<Record<number, string>>({});
  const [guardando, setGuardando] = React.useState<number | null>(null);

  const cargar = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
      const [resPend, resTraz] = await Promise.all([
        fetch(`${baseUrl}/asignacion-disenador/pendientes`, { headers }),
        fetch(`${baseUrl}/asignacion-disenador/trazabilidad`, { headers }),
      ]);
      const dataPend = await resPend.json();
      const dataTraz = await resTraz.json();
      if (!resPend.ok) throw new Error(dataPend.message || "No fue posible cargar pendientes.");
      if (!resTraz.ok) throw new Error(dataTraz.message || "No fue posible cargar trazabilidad.");
      setItems(Array.isArray(dataPend) ? dataPend : []);
      setTrazabilidad(Array.isArray(dataTraz) ? dataTraz : []);
      const init: Record<number, string> = {};
      (dataPend || []).forEach((p: Pendiente) => {
        const unico = p.disenadores_disponibles?.length === 1 ? p.disenadores_disponibles[0].usuario : "";
        init[p.proyecto_id] = unico;
      });
      setSeleccion(init);
    } catch (e: any) {
      setError(e.message || "Error al cargar.");
    } finally {
      setLoading(false);
    }
  }, [baseUrl, token]);

  React.useEffect(() => {
    cargar();
  }, [cargar]);

  const asignar = async (proyectoId: number) => {
    const disenador = seleccion[proyectoId];
    if (!disenador) {
      setError("Seleccione un diseñador.");
      return;
    }
    try {
      setGuardando(proyectoId);
      setError(null);
      setOk(null);
      const res = await fetch(`${baseUrl}/${proyectoId}/asignacion-disenador`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ disenador_usuario: disenador }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "No fue posible asignar.");
      setOk(data.mensaje || "Diseñador asignado correctamente.");
      await cargar();
    } catch (e: any) {
      setError(e.message || "Error al asignar.");
    } finally {
      setGuardando(null);
    }
  };

  const asignados = trazabilidad.filter(
    (t) => String(t.disenador_asignacion?.estado || "") === "asignado",
  );

  if (loading) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <CircularProgress size={22} />
        <Typography>Cargando asignaciones…</Typography>
      </Stack>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {ok && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {ok}
        </Alert>
      )}
      <Alert severity="info" sx={{ mb: 2 }}>
        Cuando se <strong>aprueba la solicitud del servicio</strong>, el proyecto pasa a diseño y queda pendiente de
        asignar un diseñador. El jefe de la unidad recibe un correo y debe asignarlo aquí. Si solo existe un diseñador
        registrado, se asigna automáticamente.
      </Alert>

      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
        Pendientes de asignación
      </Typography>
      {!items.length ? (
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          No hay proyectos pendientes de asignación de diseñador.
        </Typography>
      ) : (
        <Paper variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Código</TableCell>
                <TableCell>Proyecto</TableCell>
                <TableCell>Municipio</TableCell>
                <TableCell>Notas</TableCell>
                <TableCell>Diseñador</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.proyecto_id} hover>
                  <TableCell>{p.codigo}</TableCell>
                  <TableCell>{p.nombre}</TableCell>
                  <TableCell>{p.municipio || "-"}</TableCell>
                  <TableCell>
                    {p.asignacion_actual?.motivo ? (
                      <Typography variant="caption" color="text.secondary">
                        {String(p.asignacion_actual.motivo)}
                      </Typography>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      value={seleccion[p.proyecto_id] || ""}
                      onChange={(e) =>
                        setSeleccion((prev) => ({ ...prev, [p.proyecto_id]: e.target.value }))
                      }
                      disabled={p.disenadores_disponibles.length <= 1}
                    >
                      {p.disenadores_disponibles.map((d) => (
                        <MenuItem key={d.usuario} value={d.usuario}>
                          {d.nombre} ({d.usuario})
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" onClick={() => navigate(`/proyectos-panel/${p.proyecto_id}/gestion`)}>
                        Ver gestión
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={guardando !== null}
                        onClick={() => asignar(p.proyecto_id)}
                      >
                        Asignar
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
        Trazabilidad de asignaciones
      </Typography>
      {!asignados.length ? (
        <Typography color="text.secondary">Aún no hay proyectos con diseñador asignado.</Typography>
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Código</TableCell>
                <TableCell>Proyecto</TableCell>
                <TableCell>Diseñador</TableCell>
                <TableCell>Asignado</TableCell>
                <TableCell>Asignado por</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {asignados.map((t) => {
                const da = t.disenador_asignacion || {};
                return (
                  <TableRow key={t.proyecto_id} hover>
                    <TableCell>{t.codigo}</TableCell>
                    <TableCell>{t.nombre}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={labelEstadoAsignacion(da)}
                        color={chipColorEstado(da)}
                      />
                    </TableCell>
                    <TableCell>{fmtFecha(da.asignado_at)}</TableCell>
                    <TableCell>{da.asignado_por || "-"}</TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => navigate(`/proyectos-panel/${t.proyecto_id}/gestion`)}>
                        Ver gestión
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}
